import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Select,
  Stack,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import type { ApplicationAnswers, ApplicationField } from "@wwrp/shared";
import { applicationsApi } from "../api/applications.js";
import { useAuth } from "../contexts/AuthContext.js";
import { hasAtLeast } from "@wwrp/shared";
import { departmentsApi } from "../api/department-options.js";

function Fields({
  fields,
  answers,
  setAnswers,
}: {
  fields: ApplicationField[];
  answers: ApplicationAnswers;
  setAnswers: (v: ApplicationAnswers) => void;
}) {
  return (
    <Stack>
      {fields.map((field) => {
        if (field.type === "section_header")
          return (
            <Title key={field.id} order={3}>
              {field.label}
            </Title>
          );
        const answer = answers[field.id];
        const set = (value: string) =>
          setAnswers({ ...answers, [field.id]: { type: "text", value } });
        if (field.type === "single_choice")
          return (
            <Select
              key={field.id}
              label={field.label}
              description={field.description}
              required={field.required}
              data={field.options}
              value={answer?.type === "choice" ? answer.value : null}
              onChange={(v) =>
                setAnswers({ ...answers, [field.id]: { type: "choice", value: v ?? "" } })
              }
            />
          );
        if (field.type === "multi_choice")
          return (
            <Select
              key={field.id}
              label={field.label}
              description={field.description}
              required={field.required}
              data={field.options}
              value={answer?.type === "choices" ? (answer.value[0] ?? null) : null}
              onChange={(v) =>
                setAnswers({ ...answers, [field.id]: { type: "choices", value: v ? [v] : [] } })
              }
            />
          );
        if (field.type === "long_text")
          return (
            <Textarea
              key={field.id}
              label={field.label}
              description={field.description}
              required={field.required}
              minRows={4}
              autosize
              value={answer?.type === "text" ? answer.value : ""}
              onChange={(e) => set(e.currentTarget.value)}
            />
          );
        if (field.type === "number")
          return (
            <TextInput
              key={field.id}
              type="number"
              label={field.label}
              required={field.required}
              value={answer?.type === "number" ? String(answer.value) : ""}
              onChange={(e) =>
                setAnswers({
                  ...answers,
                  [field.id]: { type: "number", value: Number(e.currentTarget.value) },
                })
              }
            />
          );
        if (field.type === "date")
          return (
            <TextInput
              key={field.id}
              type="date"
              label={field.label}
              required={field.required}
              value={answer?.type === "date" ? answer.value : ""}
              onChange={(e) =>
                setAnswers({
                  ...answers,
                  [field.id]: { type: "date", value: e.currentTarget.value },
                })
              }
            />
          );
        return (
          <TextInput
            key={field.id}
            type={field.type === "email" ? "email" : "text"}
            label={field.label}
            description={field.description}
            required={field.required}
            value={answer?.type === "text" ? answer.value : ""}
            onChange={(e) => set(e.currentTarget.value)}
          />
        );
      })}
    </Stack>
  );
}

export function ApplicationsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = Boolean(
    user && (user.isSysadmin || hasAtLeast(user.role, "senior_administration")),
  );
  const templates = useQuery({
    queryKey: ["application-templates"],
    queryFn: applicationsApi.templates,
  });
  const departments = useQuery({
    queryKey: ["application-departments"],
    queryFn: departmentsApi.list,
  });
  const list = useQuery({ queryKey: ["applications"], queryFn: applicationsApi.list });
  const [selected, setSelected] = useState<string | null>(null);
  const [answers, setAnswers] = useState<ApplicationAnswers>({});
  const [review, setReview] = useState(false);
  const template = templates.data?.find((t) => String(t.id) === selected);
  const departmentName = (id: number | null) =>
    departments.data?.find((d) => d.id === id)?.name ?? "Department";
  const submit = useMutation({
    mutationFn: () => applicationsApi.submit(template!.id, answers),
    onSuccess: (v) => navigate(`/applications/${v.id}`),
  });
  return (
    <Stack className="page" gap="xl">
      <Title>Applications</Title>
      <Text c="dimmed">Request a transfer or reserve placement and follow its review.</Text>
      <Tabs defaultValue="new">
        <Tabs.List>
          <Tabs.Tab value="new">New application</Tabs.Tab>
          <Tabs.Tab value="mine">My applications</Tabs.Tab>
          <Tabs.Tab value="review">Staff review</Tabs.Tab>
          {canManage && <Tabs.Tab value="settings">Settings</Tabs.Tab>}
        </Tabs.List>
        <Tabs.Panel value="new" pt="lg">
          <Stack maw={760}>
            <Select
              label="Department and application type"
              placeholder="Choose an open application"
              data={(templates.data ?? [])
                .filter((t) => t.status === "open")
                .map((t) => ({
                  value: String(t.id),
                  label: `${departmentName(t.targetDepartmentId)} · ${t.type === "transfer" ? "Transfer" : "Reserve"} · ${t.title}`,
                }))}
              value={selected}
              onChange={(v) => {
                setSelected(v);
                setAnswers({});
                setReview(false);
              }}
            />
            {template &&
              (review ? (
                <Card>
                  <Title order={3}>Review your answers</Title>
                  {template.fields
                    .filter((f) => f.type !== "section_header")
                    .map((f) => (
                      <Text key={f.id}>
                        <b>{f.label}</b>: {JSON.stringify(answers[f.id]?.value ?? "")}
                      </Text>
                    ))}
                  <Group mt="md">
                    <Button variant="light" onClick={() => setReview(false)}>
                      Edit
                    </Button>
                    <Button loading={submit.isPending} onClick={() => submit.mutate()}>
                      Submit application
                    </Button>
                  </Group>
                </Card>
              ) : (
                <>
                  <Fields fields={template.fields} answers={answers} setAnswers={setAnswers} />
                  <Button onClick={() => setReview(true)}>Review application</Button>
                </>
              ))}
            {submit.error && <Alert color="red">{submit.error.message}</Alert>}
          </Stack>
        </Tabs.Panel>
        <Tabs.Panel value="mine" pt="lg">
          <Stack>
            {(list.data ?? []).map((a) => (
              <Card key={a.id} component={Link} to={`/applications/${a.id}`}>
                <Group justify="space-between">
                  <Text>{a.templateTitle}</Text>
                  <Badge>{a.status}</Badge>
                </Group>
                <Text size="sm" c="dimmed">
                  {a.sourceDepartmentName} → {a.targetDepartmentName}
                </Text>
              </Card>
            ))}
          </Stack>
        </Tabs.Panel>
        <Tabs.Panel value="review" pt="lg">
          <Stack>
            {(list.data ?? []).map((a) => (
              <Card key={a.id} component={Link} to={`/applications/${a.id}`}>
                <Group justify="space-between">
                  <Text>{a.templateTitle}</Text>
                  <Badge>{a.status}</Badge>
                </Group>
              </Card>
            ))}
          </Stack>
        </Tabs.Panel>
        {canManage && (
          <Tabs.Panel value="settings" pt="lg">
            <TemplateSettings
              templates={templates.data ?? []}
              departments={departments.data ?? []}
            />
          </Tabs.Panel>
        )}
      </Tabs>
    </Stack>
  );
}

function TemplateSettings({
  templates,
  departments,
}: {
  templates: Awaited<ReturnType<typeof applicationsApi.templates>>;
  departments: Awaited<ReturnType<typeof departmentsApi.list>>;
}) {
  const client = useQueryClient();
  const [id, setId] = useState<string | null>(null);
  const selected = templates.find((t) => String(t.id) === id);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [kind, setKind] = useState<"transfer" | "reserve">("transfer");
  const [department, setDepartment] = useState("");
  const [fields, setFields] = useState<ApplicationField[]>([]);
  const [status, setStatus] = useState<"draft" | "open" | "closed">("draft");
  const save = useMutation({
    mutationFn: () => {
      const body = {
        title,
        description,
        type: kind,
        targetDepartmentId: Number(department),
        status,
        fields,
      };
      return selected
        ? applicationsApi.updateTemplate(selected.id, body)
        : applicationsApi.createTemplate(body);
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ["application-templates"] }),
  });
  return (
    <Stack maw={760}>
      <Select
        label="Edit template"
        data={[
          { value: "new", label: "New template" },
          ...templates.map((t) => ({ value: String(t.id), label: t.title })),
        ]}
        value={id ?? "new"}
        onChange={(value) => {
          const t = templates.find((item) => String(item.id) === value);
          setId(t ? String(t.id) : null);
          setTitle(t?.title ?? "");
          setDescription(t?.description ?? "");
          setKind(t?.type ?? "transfer");
          setDepartment(String(t?.targetDepartmentId ?? ""));
          setFields(t?.fields ?? []);
          setStatus(t?.status ?? "draft");
        }}
      />
      <TextInput label="Title" value={title} onChange={(e) => setTitle(e.currentTarget.value)} />
      <Textarea
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.currentTarget.value)}
      />
      <Select
        label="Application type"
        data={[
          { value: "transfer", label: "Transfer" },
          { value: "reserve", label: "Reserve" },
        ]}
        value={kind}
        onChange={(v) => setKind(v as "transfer" | "reserve")}
      />
      <Select
        label="Destination department"
        data={departments.map((d) => ({ value: String(d.id), label: d.name }))}
        value={department || null}
        onChange={(v) => setDepartment(v ?? "")}
      />
      <Select
        label="Availability"
        data={["draft", "open", "closed"]}
        value={status}
        onChange={(v) => setStatus(v as typeof status)}
      />
      <Title order={3}>Questions</Title>
      {fields.map((f, index) => (
        <Card key={f.id}>
          <Group grow>
            <TextInput
              aria-label="Question"
              value={f.label}
              onChange={(e) =>
                setFields(
                  fields.map((item, i) =>
                    i === index ? { ...item, label: e.currentTarget.value } : item,
                  ),
                )
              }
            />
            <Select
              aria-label="Answer type"
              data={[
                "short_text",
                "long_text",
                "email",
                "number",
                "date",
                "single_choice",
                "multi_choice",
                "section_header",
              ]}
              value={f.type}
              onChange={(v) =>
                setFields(
                  fields.map((item, i) =>
                    i === index
                      ? ({ ...item, type: v as ApplicationField["type"] } as ApplicationField)
                      : item,
                  ),
                )
              }
            />
          </Group>
          <TextInput
            label="Help text"
            value={f.description ?? ""}
            onChange={(e) =>
              setFields(
                fields.map((item, i) =>
                  i === index ? { ...item, description: e.currentTarget.value } : item,
                ),
              )
            }
          />
          {"options" in f && (
            <TextInput
              label="Options (comma separated)"
              value={f.options.join(", ")}
              onChange={(e) =>
                setFields(
                  fields.map((item, i) =>
                    i === index
                      ? ({
                          ...item,
                          options: e.currentTarget.value
                            .split(",")
                            .map((v) => v.trim())
                            .filter(Boolean),
                        } as ApplicationField)
                      : item,
                  ),
                )
              }
            />
          )}
          <Group mt="sm">
            <Button
              variant={f.required ? "filled" : "light"}
              onClick={() =>
                setFields(
                  fields.map((item, i) =>
                    i === index ? { ...item, required: !item.required } : item,
                  ),
                )
              }
            >
              {f.required ? "Required" : "Optional"}
            </Button>
            <Button
              variant="subtle"
              disabled={index === 0}
              onClick={() => {
                const next = [...fields];
                [next[index - 1], next[index]] = [next[index]!, next[index - 1]!];
                setFields(next);
              }}
            >
              Move up
            </Button>
            <Button
              color="red"
              variant="subtle"
              onClick={() => setFields(fields.filter((item) => item.id !== f.id))}
            >
              Remove
            </Button>
          </Group>
        </Card>
      ))}
      <Button
        variant="light"
        onClick={() =>
          setFields([
            ...fields,
            { id: crypto.randomUUID(), label: "New question", required: true, type: "long_text" },
          ])
        }
      >
        Add question
      </Button>
      <Button
        disabled={!title.trim() || !department || !fields.length}
        loading={save.isPending}
        onClick={() => save.mutate()}
      >
        Save template
      </Button>
      {save.error && <Alert color="red">{save.error.message}</Alert>}
    </Stack>
  );
}

export function ApplicationDetailPage() {
  const id = Number(useParams().id),
    client = useQueryClient();
  const query = useQuery({
    queryKey: ["application", id],
    queryFn: () => applicationsApi.detail(id),
  });
  const [note, setNote] = useState(""),
    [stipulations, setStipulations] = useState(""),
    [message, setMessage] = useState(""),
    [privateNote, setPrivateNote] = useState("");
  const [answers, setAnswers] = useState<ApplicationAnswers>({});
  const action = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["application", id] });
      client.invalidateQueries({ queryKey: ["applications"] });
    },
  });
  const a = query.data;
  if (!a)
    return (
      <Stack className="page">
        <Title>Application</Title>
        {query.error && <Alert color="red">{query.error.message}</Alert>}
      </Stack>
    );
  return (
    <Stack className="page" gap="lg">
      <Title>{a.templateTitle}</Title>
      <Group>
        <Badge>{a.status}</Badge>
        <Text>
          {a.sourceDepartmentName} → {a.targetDepartmentName}
        </Text>
      </Group>
      {a.status === "approved" && (
        <Alert color="teal" title="Approved">
          Staff will handle the department changes manually.
        </Alert>
      )}
      <Card>
        <Title order={3}>Answers</Title>
        {a.fields
          .filter((f) => f.type !== "section_header")
          .map((f) => (
            <Text key={f.id}>
              <b>{f.label}</b>: {JSON.stringify(a.answers[f.id]?.value ?? "")}
            </Text>
          ))}
      </Card>
      <Group align="stretch" grow>
        {(["source_department", "target_department"] as const)
          .filter((side) => a.type === "transfer" || side === "target_department")
          .map((side) => (
            <Card key={side}>
              <Title order={4}>
                {side === "source_department" ? "Current department" : "Destination department"}
              </Title>
              <Text>
                {a.reviews.find((r) => r.side === side && r.round === a.reviewRound)?.decision ??
                  "Awaiting review"}
              </Text>
              {a.actions[side === "source_department" ? "sourceDecision" : "targetDecision"] && (
                <Stack mt="md">
                  <Textarea
                    label="Approval note or denial reason"
                    value={note}
                    onChange={(e) => setNote(e.currentTarget.value)}
                  />
                  <Textarea
                    label="Stipulations (optional)"
                    value={stipulations}
                    onChange={(e) => setStipulations(e.currentTarget.value)}
                  />
                  <Group>
                    <Button
                      onClick={() =>
                        action.mutate(() =>
                          applicationsApi.decision(id, {
                            side,
                            round: a.reviewRound,
                            decision: "approved",
                            note,
                            stipulations,
                          }),
                        )
                      }
                    >
                      Approve
                    </Button>
                    <Button
                      color="red"
                      disabled={!note.trim()}
                      onClick={() =>
                        action.mutate(() =>
                          applicationsApi.decision(id, {
                            side,
                            round: a.reviewRound,
                            decision: "denied",
                            note,
                            stipulations,
                          }),
                        )
                      }
                    >
                      Deny
                    </Button>
                  </Group>
                  <Button
                    variant="subtle"
                    onClick={() => action.mutate(() => applicationsApi.revision(id, note))}
                  >
                    Request revision
                  </Button>
                </Stack>
              )}
            </Card>
          ))}
      </Group>
      <Card>
        <Title order={3}>Discussion</Title>
        {a.messages.map((m) => (
          <Text key={m.id}>{m.body}</Text>
        ))}
        {a.actions.reply && (
          <Group mt="md">
            <TextInput
              aria-label="Reply"
              value={message}
              onChange={(e) => setMessage(e.currentTarget.value)}
            />
            <Button
              disabled={!message.trim()}
              onClick={() => action.mutate(() => applicationsApi.message(id, message))}
            >
              Reply
            </Button>
          </Group>
        )}
      </Card>
      {a.actions.privateNote && (
        <Card>
          <Title order={3}>Private staff notes</Title>
          {a.staffNotes?.map((n) => (
            <Text key={n.id}>{n.body}</Text>
          ))}
          <Textarea
            label="Add private note"
            value={privateNote}
            onChange={(e) => setPrivateNote(e.currentTarget.value)}
          />
          <Button
            disabled={!privateNote.trim()}
            onClick={() => action.mutate(() => applicationsApi.privateNote(id, privateNote))}
          >
            Add note
          </Button>
        </Card>
      )}
      {a.actions.resubmit && (
        <Card>
          <Title order={3}>Revise answers</Title>
          <Fields
            fields={a.fields}
            answers={Object.keys(answers).length ? answers : a.answers}
            setAnswers={setAnswers}
          />
          <Button
            mt="md"
            onClick={() =>
              action.mutate(() =>
                applicationsApi.resubmit(id, Object.keys(answers).length ? answers : a.answers),
              )
            }
          >
            Resubmit
          </Button>
        </Card>
      )}
      {a.actions.withdraw && (
        <Button
          color="red"
          variant="subtle"
          onClick={() => action.mutate(() => applicationsApi.withdraw(id))}
        >
          Withdraw application
        </Button>
      )}
      {action.error && <Alert color="red">{action.error.message}</Alert>}
    </Stack>
  );
}
